import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, HelpCircle, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/brand";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-200";

function GoogleButton({ onAuthenticated }) {
  const [clientId, setClientId] = useState("");

  useEffect(() => {
    fetch("/wp-json/csai/v1/auth-config", { credentials: "same-origin" })
      .then((response) => response.json())
      .then((data) => setClientId(data.google_client_id || ""))
      .catch(() => {});
  }, []);

  const startGoogle = () => {
    if (!clientId) {
      toast.info("Google sign-in needs the CreativeSolution OAuth Client ID.");
      return;
    }
    const initialize = () => {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            const response = await fetch("/wp-json/csai/v1/google-login", {
              method: "POST", credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ credential }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Google sign-in failed.");
            onAuthenticated(data.user);
          } catch (error) {
            toast.error(error.message);
          }
        },
      });
      window.google.accounts.id.prompt();
    };
    if (window.google?.accounts?.id) {
      initialize();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initialize;
    script.onerror = () => toast.error("Google sign-in could not be loaded.");
    document.head.appendChild(script);
  };

  return (
    <button
      type="button"
      data-testid="google-signin-button"
      onClick={startGoogle}
      className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
      </svg>
      Continue with Google
    </button>
  );
}

export default function Auth() {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const initialMode = ["signup", "reset", "verify"].includes(params.get("mode")) ? params.get("mode") : "login";
  const [mode, setMode] = useState(initialMode);
  const [showPass, setShowPass] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ name: "", business_name: "", email: "", phone: "", password: "", confirm_password: "" });
  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const authenticated = (user) => {
    localStorage.setItem("csai_user", JSON.stringify(user));
    const mustOnboard = user?.onboarding_complete === false;
    let destination = mustOnboard ? "/onboarding" : "/dashboard";
    const requested = params.get("redirect_to");
    if (requested) {
      try {
        const target = new URL(requested, window.location.origin);
        const allowed = ["/onboarding", "/dashboard", "/inbox"];
        const safeTarget = target.origin === window.location.origin
          && allowed.some((path) => target.pathname.startsWith(path));
        if (safeTarget && (!mustOnboard || target.pathname.startsWith("/onboarding"))) {
          destination = target.pathname + target.search;
        }
      } catch (_) {}
    }
    window.location.href = destination;
  };

  useEffect(() => {
    if (mode !== "verify") return;
    const verifyParams = new URLSearchParams(search);
    setLoading(true);
    fetch("/wp-json/csai/v1/verify-email", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: verifyParams.get("user_id"), token: verifyParams.get("token") }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Email verification failed.");
        setNotice(data.message);
        setMode("login");
      })
      .catch((error) => {
        setNotice(error.message);
        setMode("login");
      })
      .finally(() => setLoading(false));
  }, [mode, search]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !agreed) {
      toast.error("Please agree to the Terms and Privacy Policy first");
      return;
    }
    if (mode === "reset" && form.password !== form.confirm_password) {
      toast.error("Both passwords must match.");
      return;
    }
    setLoading(true);
    try {
      let endpoint;
      let payload;
      if (mode === "login") {
        endpoint = "login";
        payload = { login: form.email, password: form.password };
      } else if (mode === "signup") {
        endpoint = "register";
        payload = { name: form.name, business_name: form.business_name, email: form.email, phone: form.phone, password: form.password };
      } else if (mode === "forgot") {
        endpoint = "forgot-password";
        payload = { email: form.email };
      } else {
        endpoint = "reset-password";
        payload = { login: params.get("login"), key: params.get("key"), password: form.password };
      }
      const response = await fetch(`/wp-json/csai/v1/${endpoint}`, {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Please check your details and try again.");
      if (mode === "login") {
        toast.success("Welcome back");
        authenticated(data.user);
      } else if (mode === "signup") {
        setNotice(data.message);
        setMode("check-email");
      } else if (mode === "forgot") {
        setNotice(data.message);
        setMode("login");
      } else {
        setNotice(data.message);
        setMode("login");
        setForm((current) => ({ ...current, password: "", confirm_password: "" }));
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    setLoading(true);
    try {
      const response = await fetch("/wp-json/csai/v1/resend-verification", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const authCopy = {
    login: ["Welcome back", "Log in to check on your AI employee."],
    signup: ["Create your account", "Takes less than a minute. Business details come later."],
    forgot: ["Reset your password", "We will email you a secure reset link."],
    reset: ["Choose a new password", "Use at least 8 characters."],
    verify: ["Verifying your email", "Please wait while we verify your account."],
    "check-email": ["Check your email", "Open the verification link we sent before logging in."],
  };
  const [heading, description] = authCopy[mode] || authCopy.login;

  return (
    <div className="flex min-h-screen flex-col bg-[#FAFAF7]" data-testid="auth-page">
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-5 sm:px-6 lg:px-8">
        <Logo />
        <Link
          to="/"
          data-testid="auth-back-home-link"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4" /> Back to website
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-16 pt-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-xl shadow-slate-900/5 sm:p-9" data-testid="auth-card">
            {["login", "signup"].includes(mode) && (
            <div className="mb-7 grid grid-cols-2 rounded-xl bg-slate-100 p-1" data-testid="auth-mode-toggle">
              {["login", "signup"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${
                    mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {m === "login" ? "Login" : "Create account"}
                </button>
              ))}
            </div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, x: mode === "login" ? -14 : 14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="auth-heading">{heading}</h1>
                <p className="mt-1.5 text-sm text-slate-500">{description}</p>

                {notice && (
                  <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="auth-notice">
                    {notice}
                  </div>
                )}

                {!["check-email", "verify"].includes(mode) && (
                  <form onSubmit={submit} className="mt-7 space-y-4">
                    {mode === "signup" && (
                      <>
                        <input data-testid="signup-fullname-input" required placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} />
                        <input data-testid="signup-business-name-input" required placeholder="Business name" value={form.business_name} onChange={update("business_name")} className={inputCls} />
                      </>
                    )}
                    {["login", "signup", "forgot"].includes(mode) && (
                      <input data-testid="auth-email-input" type="email" required placeholder={mode === "signup" ? "Work email" : "Email address"} value={form.email} onChange={update("email")} className={inputCls} />
                    )}
                    {mode === "signup" && (
                      <div className="flex" data-testid="signup-mobile-group">
                        <span className="flex items-center rounded-l-xl border border-r-0 border-slate-200 bg-slate-50 px-3.5 text-sm font-medium text-slate-600">+91</span>
                        <input data-testid="signup-mobile-input" required inputMode="tel" placeholder="Mobile number" value={form.phone} onChange={update("phone")} className={`${inputCls} rounded-l-none`} />
                      </div>
                    )}
                    {["login", "signup", "reset"].includes(mode) && (
                      <div className="relative">
                        <input data-testid="auth-password-input" type={showPass ? "text" : "password"} required minLength={8} placeholder={mode === "reset" ? "New password" : "Password"} value={form.password} onChange={update("password")} className={`${inputCls} pr-12`} />
                        <button type="button" data-testid="password-visibility-toggle" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Toggle password visibility">
                          {showPass ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    )}
                    {mode === "reset" && (
                      <input data-testid="confirm-password-input" type={showPass ? "text" : "password"} required minLength={8} placeholder="Confirm new password" value={form.confirm_password} onChange={update("confirm_password")} className={inputCls} />
                    )}
                    {mode === "login" && (
                      <div className="text-right">
                        <button type="button" data-testid="forgot-password-link" onClick={() => { setNotice(""); setMode("forgot"); }} className="text-sm font-medium text-orange-700 hover:underline">
                          Forgot password?
                        </button>
                      </div>
                    )}
                    {mode === "signup" && (
                      <label className="flex items-start gap-3" data-testid="signup-terms-checkbox">
                        <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
                        <span className="text-xs leading-relaxed text-slate-500">
                          I agree to the <span className="font-medium text-orange-700">Terms and Conditions</span> and <span className="font-medium text-orange-700">Privacy Policy</span>
                        </span>
                      </label>
                    )}
                    <button type="submit" data-testid="auth-submit-button" disabled={loading} className="w-full rounded-xl bg-orange-600 px-5 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700 hover:shadow disabled:cursor-wait disabled:opacity-60">
                      {loading ? "Please wait..." : mode === "login" ? "Login" : mode === "signup" ? "Create Account" : mode === "forgot" ? "Email reset link" : "Update password"}
                    </button>
                    {["forgot", "reset"].includes(mode) && (
                      <button type="button" onClick={() => { setNotice(""); setMode("login"); }} className="w-full text-sm font-semibold text-orange-700 hover:underline">Back to login</button>
                    )}
                  </form>
                )}

                {mode === "check-email" && (
                  <div className="mt-7 space-y-3">
                    <button type="button" onClick={resendVerification} disabled={loading} className="w-full rounded-xl bg-orange-600 px-5 py-3.5 text-sm font-semibold text-white disabled:opacity-60">
                      {loading ? "Sending..." : "Resend verification email"}
                    </button>
                    <button type="button" onClick={() => setMode("login")} className="w-full text-sm font-semibold text-orange-700 hover:underline">Back to login</button>
                  </div>
                )}

                {["login", "signup"].includes(mode) && (
                  <>
                    <div className="my-6 flex items-center gap-4">
                      <span className="h-px flex-1 bg-slate-200" />
                      <span className="text-xs font-medium text-slate-400">or</span>
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>
                    <GoogleButton onAuthenticated={authenticated} />
                    <p className="mt-6 text-center text-sm text-slate-500">
                      {mode === "login" ? (
                        <>New here?{" "}<button data-testid="switch-to-signup-link" onClick={() => { setNotice(""); setMode("signup"); }} className="font-semibold text-orange-700 hover:underline">Create account</button></>
                      ) : (
                        <>Already have an account?{" "}<button data-testid="switch-to-login-link" onClick={() => { setNotice(""); setMode("login"); }} className="font-semibold text-orange-700 hover:underline">Login</button></>
                      )}
                    </p>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            data-testid="auth-help-link"
            onClick={() => toast.info("Design preview: help center opens here")}
            className="mx-auto mt-6 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-orange-700"
          >
            <HelpCircle className="h-4 w-4" /> Need help? Contact support
          </button>
        </motion.div>
      </div>
    </div>
  );
}
